import { clickOcassionsProceedToPayCTA, clickProceedToPay } from "@cart-client/analytics/events";
import { DEFAULT_PAYMENTS_RESPONSE_KEY } from "@cart-client/constants/StorageKeys";
import { CartContext } from "@cart-client/contexts/CartContext";
import { isBulkOrder } from "@cart-client/helpers/BulkOrder";
import { isCartTotalZero } from "@cart-client/helpers/CartItemRenderer";
import { syncCartAddressHeaders } from "@cart-client/helpers/Header";
import Blaze from "@swiggy-private/blaze.js";
import {
    AnalyticsContext,
    BLAZE_EVENTS,
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
    getBridgeSdk,
    GlobalHeaderState,
    GroupOrderCartContext,
    SwgyHeaderKeysList,
    useNavigate,
} from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";
import { useCallback, useContext, useMemo } from "react";

interface UseCartPaymentReturnType {
    cartFooterAction: VoidFunction;
    canPlaceOrderFromCart: boolean;
    onMakePayment: (selectedSlot?: string) => void;
}

const useCartPayment = (): UseCartPaymentReturnType => {
    const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
    const isFromBottomBar = GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar) === "true";
    const { setCheckoutState } = useContext(CheckoutContext);
    const { state: groupCartState } = useContext(GroupOrderCartContext);
    const { cartState } = useContext(CartContext);
    const { clickEvent } = useContext(AnalyticsContext);

    const { reviewedCart: cartResponse, localCart } = cartState;
    const deliverySlots = cartResponse?.data?.slot_details;

    const isBulkOrderCart = useMemo(() => {
        return isBulkOrder(cartResponse);
    }, [cartResponse]);

    const navigateTo = useNavigate();

    // If cart total is 0, we will direclty place the order from cart itself
    const canPlaceOrderFromCart = useMemo(() => isCartTotalZero(cartResponse), [cartResponse]);

    const gotoPayment = useCallback(() => {
        // log payment start intreaction time, any change here should be reflected in `packages/payment-client/src/components/payment-main/PaymentMain.tsx` as well
        Blaze.recordStart(BLAZE_EVENTS.RECORD_CHECKOUT_PAYMENT_LOAD_TIME);

        // clear cached payment data before goiing to payment screen from web cart
        // We also clear cache data if going to payment page from `Change` cta on turbo widget
        if (isSessionStorageAvailable) {
            sessionStorage.removeItem(DEFAULT_PAYMENTS_RESPONSE_KEY);
            sessionStorage.removeItem("with_coupon");
            sessionStorage.setItem("isFromWebCart", "true");
        }

        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({
                show: false,
            });
        }

        setCheckoutState({
            type: CheckoutActionTypes.SHOW_PAYMENTS,
        });
        navigateTo(CheckoutRoute.PAYMENTS, {}, false);
    }, [isFromBottomBar, isSessionStorageAvailable, navigateTo, setCheckoutState]);

    const placeOrderFromCart = useCallback(() => {
        // clear cached payment data before goiing to payment screen from web cart
        if (isSessionStorageAvailable) {
            sessionStorage.removeItem(DEFAULT_PAYMENTS_RESPONSE_KEY);
            sessionStorage.removeItem("with_coupon");
            sessionStorage.setItem("isFromWebCart", "true");
        }

        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({
                show: false,
            });
        }
        const cartTotal = cartResponse?.data?.cart_total;
        setCheckoutState({
            type: CheckoutActionTypes.DIRECT_PLACE_ORDER,
            payload: {
                fromCart: true,
                cartTotal: cartTotal || 0,
                orderComment: localCart?.orderComment || "",
            },
        });
    }, [isSessionStorageAvailable, isFromBottomBar, setCheckoutState, cartResponse, localCart]);

    // Footer action can be either go to payment screen or directly place order from cart in case of 0 amount to pay
    const cartFooterAction = useMemo(() => (canPlaceOrderFromCart ? placeOrderFromCart : gotoPayment), [
        canPlaceOrderFromCart,
        gotoPayment,
        placeOrderFromCart,
    ]);

    const onMakePayment = useCallback(
        (selectedSlot?: string) => {
            // analytics event
            if (!isEmpty(deliverySlots)) {
                if (isBulkOrderCart) {
                    const context = {
                        cartId: cartResponse?.data?.cartId,
                        bulkOrder: true,
                        cartAmount: cartResponse?.data?.cart_total ?? 0,
                        selectedSlot: selectedSlot,
                    };
                    clickOcassionsProceedToPayCTA(
                        clickEvent,
                        cartResponse?.data?.restaurant_details?.id,
                        JSON.stringify(context),
                    );
                } else {
                    const slotContext = {
                        cartId: cartResponse?.data?.cartId,
                        slotSelected: deliverySlots?.slots?.filter(slot => slot.available === true)[0],
                    };
                    clickProceedToPay(clickEvent, {
                        cartAmount: cartResponse?.data?.cart_total ?? 0,
                        restaurantId: cartResponse?.data?.restaurant_details?.id,
                        context: JSON.stringify(slotContext),
                        groupCartId: groupCartState?.data?.group_cart_id,
                    });
                }
            } else {
                clickProceedToPay(clickEvent, {
                    cartAmount: cartResponse?.data?.cart_total ?? 0,
                    restaurantId: cartResponse?.data?.restaurant_details?.id,
                    groupCartId: groupCartState?.data?.group_cart_id,
                });
            }

            // sync client headers before navigating to payments
            // update cartAddressId && cartId

            const cartId = cartResponse?.data?.cartId || "";
            const addressId = cartResponse?.data?.address_id || "";

            syncCartAddressHeaders({ cartId, addressId });

            cartFooterAction();
        },
        [
            deliverySlots,
            cartResponse?.data?.cartId,
            cartResponse?.data?.address_id,
            cartResponse?.data?.cart_total,
            cartResponse?.data?.restaurant_details?.id,
            isBulkOrderCart,
            clickEvent,
            groupCartState?.data?.group_cart_id,
            cartFooterAction,
        ],
    );

    return {
        cartFooterAction,
        canPlaceOrderFromCart,
        onMakePayment,
    };
};

export default useCartPayment;
